import type { Draft } from "immer";

import { createZustandContext } from "@/lib/store/zustand-context-pattern";
import {
  type StoreCreatorWithMiddlewares,
  createWithMiddlewares,
} from "@/lib/store/zustand-with-middlewares";

import {
  createActionsSlice,
  createStateSlice,
  createUtilitiesSlice,
} from "./slices";
import type { FormStore, ImmerSetFunction, SetAdapter } from "./types";

/** immerセット関数をスライス向けのセット関数に変換するアダプタ */
const createSetAdapter = (
  rawSet: (
    fn: (state: Draft<FormStore>) => void,
    replace?: boolean,
    action?: string | object,
  ) => void,
): SetAdapter => ({
  sliceSet: (
    partialOrFn: Partial<FormStore> | ((draft: Draft<FormStore>) => void),
    replace?: boolean,
    action?: string | object,
  ) => {
    if (typeof partialOrFn === "function") {
      rawSet(partialOrFn, replace, action);
    } else {
      rawSet(
        (state) => {
          Object.assign(state, partialOrFn);
        },
        replace,
        action,
      );
    }
  },
  rawSet: (fn) => {
    rawSet(fn);
  },
});

/** フォームストアの初期値を提供するクリエーター  */
const formStoreCreator: StoreCreatorWithMiddlewares<FormStore> = (
  set,
  get,
  api,
) => {
  const state = createStateSlice({
    form: undefined,
    values: [],
    touched: {},
    errors: [],
    isSubmitted: false,
    isSubmitAttempted: false,
  });

  // アダプターパターンを使用して型安全にset関数をラップ
  const adapter = createSetAdapter(set as ImmerSetFunction);

  // 各スライスにアダプターのsliceSetを渡す
  const actions = createActionsSlice(adapter.sliceSet, get, api);
  const utilities = createUtilitiesSlice(adapter.sliceSet, get, api);

  // すべてのスライスを結合
  return {
    ...state,
    ...actions,
    ...utilities,
  };
};

/** FormStoreのコンテキストとフックを作成 */
export const { context: FormStoreContext, useStore: useFormStoreContext } =
  createZustandContext<FormStore>(formStoreCreator, {
    name: "FormFactory Store",
    enabled: true,
  });

/** グローバルフォームストアインスタンスを作成する */
export const createFormStore = () => {
  return createWithMiddlewares<FormStore>(formStoreCreator, undefined, {
    name: "FormFactory Store",
    enabled: true,
  });
};
